import React, { useEffect, useRef } from 'react';
import { Phone, PhoneOff, Loader2 } from 'lucide-react';

import Modal from '../ui/Modal';
import { useBrowserVoiceTest } from './useBrowserVoiceTest';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

interface WorkflowTestPanelProps {
  open: boolean;
  workflowId?: string;
  workflowName?: string;
  onClose: () => void;
}

export const WorkflowTestPanel: React.FC<WorkflowTestPanelProps> = ({
  open,
  workflowId,
  workflowName,
  onClose,
}) => {
  const { status, transcript, error, start, end } = useBrowserVoiceTest();
  const scrollRef = useRef<HTMLDivElement>(null);

  const active = status === 'connecting' || status === 'connected';

  useEffect(() => {
    // End the call if the modal is closed while a call is active.
    if (!open && active) end();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [transcript]);

  const handleClose = () => {
    if (active) end();
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={`Test${workflowName ? `: ${workflowName}` : ''}`}
      size="md"
    >
      <div className="flex flex-col gap-4">
        {status === 'idle' || status === 'ended' || status === 'error' ? (
          <div className="flex flex-col items-start gap-3 py-4">
            <div className="rounded-lg bg-gray-100 p-2 text-gray-700">
              <Phone className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-semibold text-gray-900">Call this agent in the browser</div>
              <p className="mt-1 text-sm text-gray-600">
                Talk to the agent over a live voice call to validate the full workflow. Telephony-only
                actions like call transfer are not performed here. Use headphones for the best experience.
              </p>
            </div>
            {error ? <div className="text-sm text-red-600">{error}</div> : null}
            <Button tone="custom"
              type="button"
              disabled={!workflowId}
              onClick={() => workflowId && start(workflowId)}
              className="mt-1 inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-white hover:bg-gray-800 disabled:opacity-50"
            >
              <Phone className="h-4 w-4" />
              {status === 'error' || status === 'ended' ? 'Retry Test' : 'Run Test'}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                Live Transcript
              </div>
              <Badge tone="success" className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium">
                <span className="h-2 w-2 rounded-full bg-green-500" />
                {status === 'connecting' ? 'Connecting…' : 'Live'}
              </Badge>
            </div>

            <div
              ref={scrollRef}
              className="h-72 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3"
            >
              {transcript.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  {status === 'connecting' ? 'Connecting…' : 'Start speaking…'}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {transcript.map((t, i) => (
                    <div
                      key={i}
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                        t.role === 'assistant'
                          ? 'self-start bg-white text-gray-800 shadow-sm'
                          : 'self-end bg-blue-600 text-white'
                      }`}
                    >
                      {t.text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-1.5 text-sm text-green-600">
                {status === 'connecting' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                )}
                {status === 'connecting' ? 'Connecting' : 'Connected'}
              </div>
              <Button tone="custom"
                type="button"
                onClick={end}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2.5 font-medium text-white hover:bg-red-700"
              >
                <PhoneOff className="h-4 w-4" />
                End Call
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default WorkflowTestPanel;
